import { useLingui } from '@lingui/react/macro'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { PLACEHOLDER_MAP } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/constants'
import type {
  DocChatTurn,
  ViewMode,
} from 'features/knowledgeDocumentViewer/types'
import type { FavoriteSearch } from 'features/knowledgeDocumentViewer/utils/favoriteSearchesStorage'
import type { SearchQueryHistoryEntry } from 'features/knowledgeDocumentViewer/utils/searchQueryHistoryStorage'
import React from 'react'
import type { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'

import { DocChatResultsHeader } from './DocChatResultsHeader'
import { SearchToolbar } from './SearchToolbar'

type SearchInputChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>

export interface UnifiedSearchHeaderProps {
  isLinkedDocsMode: boolean
  isDocChatExperience: boolean
  contentFilter: DocumentsViewerContentFilter
  searchInput: string
  onSearchChange: (event: SearchInputChangeEvent) => void
  onSearchKeyDown: (event: React.KeyboardEvent) => void
  onSearchSubmit: () => void
  viewMode: ViewMode
  onViewModeChange: (value: string | number) => void
  suggestions: SearchQueryHistoryEntry[]
  onSuggestionSelect: (query: string) => void
  onClearHistory: () => void
  favoriteSearches: FavoriteSearch[]
  isSearchFavorited: (query: string) => boolean
  onToggleSearchFavorite: (query: string, contentFilter: string) => void
  docChatTurns: DocChatTurn[]
  onDocChatKeyDown: (event: React.KeyboardEvent) => void
  onDocChatSubmit: () => void
  canSubmitDocChat?: boolean
  isDocChatGenerating?: boolean
  onDocChatStop?: () => void
}

export function UnifiedSearchHeader({
  isLinkedDocsMode,
  isDocChatExperience,
  contentFilter,
  searchInput,
  onSearchChange,
  onSearchKeyDown,
  onSearchSubmit,
  suggestions,
  onSuggestionSelect,
  onClearHistory,
  favoriteSearches,
  isSearchFavorited,
  onToggleSearchFavorite,
  docChatTurns,
  onDocChatKeyDown,
  onDocChatSubmit,
  canSubmitDocChat = true,
  isDocChatGenerating = false,
  onDocChatStop,
}: UnifiedSearchHeaderProps): React.ReactElement {
  const { searchMode, handleSearchInputChange } = useAdvancedSearchUX()
  const { i18n } = useLingui()

  if (isLinkedDocsMode) {
    return <></>
  }

  if (isDocChatExperience && searchMode === 'ai') {
    return (
      <DocChatResultsHeader
        turns={docChatTurns}
        searchInput={searchInput}
        onSearchChange={handleSearchInputChange}
        onSearchKeyDown={onDocChatKeyDown}
        onSearchSubmit={onDocChatSubmit}
        contentFilter={contentFilter}
        submitDisabled={!canSubmitDocChat}
        isGenerating={isDocChatGenerating}
        onStop={onDocChatStop}
      />
    )
  }

  return (
    <SearchToolbar
      searchInput={searchInput}
      onSearchChange={onSearchChange}
      onSearchKeyDown={onSearchKeyDown}
      onSearchSubmit={onSearchSubmit}
      placeholder={i18n._(PLACEHOLDER_MAP[contentFilter])}
      suggestions={suggestions}
      onSuggestionSelect={onSuggestionSelect}
      onClearHistory={onClearHistory}
      favoriteSearches={favoriteSearches}
      isSearchFavorited={isSearchFavorited}
      onToggleSearchFavorite={onToggleSearchFavorite}
      contentFilter={contentFilter}
    />
  )
}
